import React, { useContext, useEffect, useState } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import BankAccountForm from "./BankAccountForm";
import CardForm from "./CardForm";
import PayoutMethodSection from "./PayoutMethodSection";
import { NotificationType, SERVER_URL } from "../../constants/global";
import { Box, Button, IconButton } from "@mui/material";
import { useQueryUniqueUsers } from "../../queries/pharmacy/hooks/useQueryUniqueUsers";
import { UserContext } from "../../hooks/userContext";
import { useAuth } from "../../hooks/useAuth";
import Link from "@mui/material/Link";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { useSearchParams } from "react-router-dom";
import BrandLoader from "../../components/shared/BrandLoader/BrandLoader";
import { ToasterContext } from "../../components/shared/Toaster/ToasterContext";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { pageTitleSx } from "../../constants/designTokens";

const INK = "#1E293B";
const INK_SOFT = "#475569";
const LINE = "#E2E8F0";
const LINE_SOFT = "#F1F5F9";
const PRIMARY = "#2563EB";
const PRIMARY_DARK = "#1D4ED8";
const BODY_FONT = '"Source Sans 3", sans-serif';
const DISPLAY_FONT = '"Lexend", sans-serif';
const MONO_FONT = '"Spline Sans Mono", monospace';

const pageShellSx = {
  bgcolor: "#FFFFFF",
  border: `1px solid ${LINE}`,
  borderRadius: "14px",
  boxShadow: "0 1px 2px rgba(15,23,42,0.06), 0 8px 24px rgba(15,23,42,0.07)",
  overflow: "hidden",
  fontFamily: BODY_FONT,
};

const stripePublicKey: string = process.env.REACT_APP_STRIPE_PUBLIC_KEY || "";
const stripePromise = loadStripe(stripePublicKey);

type PaymentMethodType = "us_bank_account" | "card";

interface PaymentMethodCardProps {
  selected: boolean;
  onSelect: () => void;
  icon: React.ReactNode;
  title: string;
  description: string;
}

const StatusTag = ({ label, tone }: { label: string; tone: "green" | "blue" }) => {
  const tones = {
    green: { color: "#2E7D32", bg: "#E8F5E9", dot: "#66BB6A" },
    blue: { color: PRIMARY_DARK, bg: "#F1F5F9", dot: PRIMARY },
  } as const;
  const t = tones[tone];
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        px: "8px",
        py: "3px",
        borderRadius: "5px",
        fontFamily: MONO_FONT,
        fontSize: "10.5px",
        fontWeight: 600,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        color: t.color,
        backgroundColor: t.bg,
      }}
    >
      <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: t.dot, flexShrink: 0 }} />
      {label}
    </Box>
  );
};

const DetailRow = ({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: 2,
      py: 1.25,
      borderBottom: `1px solid ${LINE_SOFT}`,
      "&:last-of-type": { borderBottom: "none" },
    }}
  >
    <Box
      sx={{
        fontSize: "11px",
        fontWeight: 600,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: INK_SOFT,
        pt: 0.25,
        flexShrink: 0,
      }}
    >
      {label}
    </Box>
    <Box
      sx={{
        fontFamily: mono ? MONO_FONT : BODY_FONT,
        fontSize: mono ? "13px" : "14px",
        fontWeight: 600,
        color: INK,
        textAlign: "right",
        wordBreak: "break-word",
      }}
    >
      {value}
    </Box>
  </Box>
);

const PageHeader = ({ title, subtitle }: { title: string; subtitle?: string }) => (
  <Box
    sx={{
      px: 3,
      py: 2.5,
      borderBottom: `1px solid ${LINE_SOFT}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 2,
    }}
  >
    <Box>
      <Box
        sx={{
          ...pageTitleSx,
        }}
      >
        {title}
      </Box>
      {subtitle && (
        <Box sx={{ fontSize: 13, color: INK_SOFT, mt: 0.25 }}>{subtitle}</Box>
      )}
    </Box>
  </Box>
);

const PaymentMethodCard: React.FunctionComponent<PaymentMethodCardProps> = ({
  selected,
  onSelect,
  icon,
  title,
  description,
}) => (
  <Box
    component="button"
    type="button"
    onClick={onSelect}
    sx={{
      p: 2.5,
      width: "100%",
      textAlign: "left",
      cursor: "pointer",
      border: `1.5px solid ${selected ? PRIMARY : LINE}`,
      borderRadius: "10px",
      bgcolor: selected ? "#F1F5F9" : "#FFFFFF",
      transition: "border-color 0.15s, background-color 0.15s",
      fontFamily: BODY_FONT,
      "&:hover": {
        borderColor: selected ? PRIMARY_DARK : "#475569",
        bgcolor: selected ? "#F1F5F9" : "#F8FAFC",
      },
      "&:focus-visible": {
        outline: `2px solid ${PRIMARY}`,
        outlineOffset: 2,
      },
    }}
  >
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 40,
          height: 40,
          borderRadius: "8px",
          bgcolor: selected ? PRIMARY : LINE_SOFT,
          color: selected ? "#FFFFFF" : INK_SOFT,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ fontWeight: 600, fontSize: 15, color: INK, lineHeight: 1.3 }}>{title}</Box>
        <Box sx={{ fontSize: 13, color: INK_SOFT, mt: 0.5, lineHeight: 1.4 }}>{description}</Box>
      </Box>
    </Box>
  </Box>
);

interface SetupIntentResponse {
  client_secret: string;
  BankAccount: { id: number };
}

export const BankAccount: React.FunctionComponent = () => {
  const { id: userId }: any = useContext(UserContext);
  const { addToast } = useContext(ToasterContext);
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get("required");
  const section = searchParams.get("section");
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [selectedType, setSelectedType] = useState<PaymentMethodType>("us_bank_account");
  const [setupIntentData, setSetupIntentData] = useState<SetupIntentResponse | null>(null);
  const [setupIntentLoading, setSetupIntentLoading] = useState<boolean>(false);
  const [isRefreshingPaymentMethod, setIsRefreshingPaymentMethod] = useState(false);

  const {
    data: pharmacyUsersData,
    loading: pharmacyUsersLoading,
    refetch: refetchPharmacyUser,
  } = useQueryUniqueUsers({
    variables: { where: { id: +userId } },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const handlePaymentMethodSaved = async () => {
    setIsRefreshingPaymentMethod(true);
    setIsEdit(false);
    try {
      await refetchPharmacyUser();
    } finally {
      setIsRefreshingPaymentMethod(false);
    }
  };

  const pharmacyUser = pharmacyUsersData?.findUniqueUser?.PharmacyUsers?.[0];
  const pharmacy = pharmacyUser?.Pharmacy;
  const pharmacyId = pharmacyUser?.pharmacyId;
  const cardPaymentEnabled = pharmacy?.cardPaymentEnabled ?? false;

  const bankAccount = pharmacy?.BankAccount?.find(
    (item: any) => item.object === "payment_method" || item.object === "card"
  );

  const hasSavedMethod = Boolean(bankAccount && bankAccount.accountId);
  const showForm = !hasSavedMethod || isEdit;

  useEffect(() => {
    if (isEdit && bankAccount?.object === "card" && cardPaymentEnabled) {
      setSelectedType("card");
    }
  }, [isEdit, bankAccount?.object, cardPaymentEnabled]);

  useEffect(() => {
    if (!cardPaymentEnabled && selectedType === "card") {
      setSelectedType("us_bank_account");
    }
  }, [cardPaymentEnabled, selectedType]);

  useEffect(() => {
    if (!showForm || !token) return;
    let cancelled = false;

    const fetchSetupIntent = async () => {
      setSetupIntentLoading(true);
      try {
        const response = await fetch(`${SERVER_URL}/stripe/setup-intent?type=${selectedType}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        if (!cancelled) setSetupIntentData(data);
      } catch {
        if (!cancelled) {
          addToast({
            type: NotificationType.Error,
            title: "Failed to initialize payment setup. Please try again.",
          });
        }
      } finally {
        if (!cancelled) setSetupIntentLoading(false);
      }
    };

    fetchSetupIntent();
    return () => {
      cancelled = true;
    };
  }, [selectedType, showForm, token, addToast]);

  useEffect(() => {
    if (tab === "true") {
      addToast({
        type: NotificationType.Error,
        title: "You are required to add your bank information before you can make a purchase.",
      });
    }
  }, [tab, addToast]);

  useEffect(() => {
    if (section !== "payout" || pharmacyUsersLoading) return;
    const timeoutId = setTimeout(() => {
      document.getElementById("payout-method-section")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 150);
    return () => clearTimeout(timeoutId);
  }, [section, pharmacyUsersLoading, pharmacy]);

  if (pharmacyUsersLoading || isRefreshingPaymentMethod) {
    return (
      <Box sx={{ ...pageShellSx, p: 6, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, minHeight: 240 }}>
        <BrandLoader size={48} />
        <Box sx={{ fontSize: 14, color: INK_SOFT }}>
          {isRefreshingPaymentMethod ? "Updating payment method…" : "Loading…"}
        </Box>
      </Box>
    );
  }

  const renderPaymentMethod = () => {
    if (hasSavedMethod && !isEdit) {
      const status = bankAccount.status;
      const isCard = bankAccount.object === "card";
      const statusLabel = isCard ? "Verified" : status === "PENDING" ? "Pending" : "Approved";
      const statusTone = status === "PENDING" ? "blue" : "green";

      return (
        <>
          <Box
            sx={{
              px: 3,
              py: 2.5,
              borderBottom: `1px solid ${LINE_SOFT}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "8px",
                  bgcolor: "#F1F5F9",
                  color: PRIMARY,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {isCard ? <CreditCardIcon /> : <AccountBalanceIcon />}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    fontFamily: DISPLAY_FONT,
                    fontWeight: 700,
                    fontSize: 20,
                    letterSpacing: "-0.01em",
                    color: INK,
                    lineHeight: 1.3,
                  }}
                >
                  Payment Method
                </Box>
                <Box sx={{ fontSize: 13, color: INK_SOFT, mt: 0.25 }}>
                  {isCard ? "Credit / debit card for purchases" : "Bank account for purchases"}
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <StatusTag label={statusLabel} tone={statusTone} />
              <IconButton
                aria-label="Edit payment method"
                onClick={() => setIsEdit(true)}
                sx={{
                  color: INK_SOFT,
                  borderRadius: "8px",
                  border: `1px solid ${LINE}`,
                  "&:hover": { color: PRIMARY, bgcolor: "#F1F5F9", borderColor: PRIMARY },
                }}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ p: 3, bgcolor: "#F8FAFC" }}>
            <Box
              sx={{
                bgcolor: "#FFFFFF",
                border: `1px solid ${LINE}`,
                borderRadius: "10px",
                p: 2.5,
                mb: 2,
              }}
            >
              {isCard ? (
                <>
                  <DetailRow label="Brand" value={bankAccount.bankName} />
                  <DetailRow label="Name on card" value={bankAccount.accountHolderName} />
                  <DetailRow label="Card number" value={`•••• ${bankAccount.last4}`} mono />
                </>
              ) : (
                <>
                  <DetailRow label="Bank" value={bankAccount.bankName} />
                  <DetailRow label="Name" value={bankAccount.accountHolderName} />
                  <DetailRow label="Country" value={bankAccount.country} />
                  <DetailRow label="Account number" value={`•••• ${bankAccount.last4}`} mono />
                  <DetailRow label="Routing number" value={bankAccount.routingNumber} mono />
                </>
              )}
            </Box>

            {status === "PENDING" && bankAccount.hostedVerificationUrl && (
              <Box sx={{ mb: 2 }}>
                <Button
                  component={Link}
                  href={bankAccount.hostedVerificationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  sx={{
                    bgcolor: PRIMARY,
                    color: "#FFFFFF",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontFamily: BODY_FONT,
                    textTransform: "none",
                    boxShadow: "none",
                    textDecoration: "none",
                    "&:hover": { bgcolor: PRIMARY_DARK, boxShadow: "none", textDecoration: "none" },
                  }}
                >
                  Verify Bank Account
                </Button>
              </Box>
            )}

            <Box
              sx={{
                p: 2,
                border: `1px solid ${LINE}`,
                borderRadius: "10px",
                bgcolor: "#FFFFFF",
              }}
            >
              <Box
                sx={{
                  fontSize: "11px",
                  fontWeight: 600,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: INK_SOFT,
                  mb: 1,
                }}
              >
                Note
              </Box>
              <Box sx={{ fontSize: 14, lineHeight: 1.55, color: INK }}>
                This {isCard ? "card" : "bank account"} is for paying. Only one payment method can be
                active at a time — switching will replace the current one.
              </Box>
            </Box>
          </Box>
        </>
      );
    }

    return (
      <>
        <PageHeader
          title="Payment Method"
          subtitle={
            cardPaymentEnabled
              ? "Choose how you want to pay for purchases"
              : "Add your US bank account for purchases"
          }
        />

        <Box sx={{ p: 3, bgcolor: "#F8FAFC" }}>
          {cardPaymentEnabled && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
                mb: 3,
              }}
            >
              <PaymentMethodCard
                selected={selectedType === "us_bank_account"}
                onSelect={() => setSelectedType("us_bank_account")}
                icon={<AccountBalanceIcon />}
                title="ACH / Bank Account"
                description="Pay with your US bank account via ACH."
              />
              <PaymentMethodCard
                selected={selectedType === "card"}
                onSelect={() => setSelectedType("card")}
                icon={<CreditCardIcon />}
                title="Credit / Debit Card"
                description="Pay with a US-issued credit or debit card."
              />
            </Box>
          )}

          <Box
            sx={{
              bgcolor: "#FFFFFF",
              border: `1px solid ${LINE}`,
              borderRadius: "10px",
              p: { xs: 2, sm: 3 },
            }}
          >
            {setupIntentLoading || !setupIntentData?.client_secret ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <BrandLoader size={48} />
              </Box>
            ) : (
              <Elements stripe={stripePromise} key={setupIntentData.client_secret}>
                {selectedType === "us_bank_account" ? (
                  <BankAccountForm
                    userId={+userId}
                    pharmacy={pharmacyUser}
                    clientSecret={setupIntentData.client_secret}
                    bankAccountId={setupIntentData.BankAccount.id}
                    pharmacyUsersLoading={pharmacyUsersLoading}
                    setIsEdit={setIsEdit}
                    onCancel={() => setIsEdit(false)}
                    onPaymentMethodSaved={handlePaymentMethodSaved}
                    oldAccountId={bankAccount?.accountId}
                  />
                ) : (
                  <CardForm
                    userId={+userId}
                    pharmacy={pharmacyUser}
                    clientSecret={setupIntentData.client_secret}
                    bankAccountId={setupIntentData.BankAccount.id}
                    pharmacyUsersLoading={pharmacyUsersLoading}
                    setIsEdit={setIsEdit}
                    onCancel={() => setIsEdit(false)}
                    onPaymentMethodSaved={handlePaymentMethodSaved}
                    oldAccountId={bankAccount?.accountId}
                  />
                )}
              </Elements>
            )}
          </Box>
        </Box>
      </>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Box sx={pageShellSx}>{renderPaymentMethod()}</Box>

      {pharmacyId && (
        <Box sx={pageShellSx}>
          <PayoutMethodSection
            pharmacy={pharmacy}
            pharmacyId={pharmacyId}
            onUpdated={refetchPharmacyUser}
          />
        </Box>
      )}
    </Box>
  );
};

export default BankAccount;
